// src/app/hooks/useInsights.ts
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { fetchInsights, saveInsight, deleteInsight } from '@/app/services/reporting/insights-api-service';
import type { InsightCreatePayload, SavedInsight } from '../../components/reporting/types';

interface UseInsightsResult {
  insights:      SavedInsight[];
  isLoading:     boolean;
  error:         Error | null;
  addInsight:    (payload: InsightCreatePayload) => Promise<SavedInsight | null>;
  removeInsight: (id: number) => Promise<void>;
  refetch:       () => void;
}

export function useInsights(brandName: string): UseInsightsResult {
  const [insights,  setInsights]  = useState<SavedInsight[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error,     setError]     = useState<Error | null>(null);
  const [tick,      setTick]      = useState(0);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!brandName) return;
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    fetchInsights(brandName)
      .then((data) => { if (!cancelled) setInsights(data); })
      .catch((err) => { if (!cancelled) setError(err instanceof Error ? err : new Error('Failed to load insights')); })
      .finally(() => { if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; };
  }, [brandName, tick]);

  const addInsight = useCallback(async (payload: InsightCreatePayload): Promise<SavedInsight | null> => {
    try {
      const saved = await saveInsight(payload);
      setInsights((prev) => [saved, ...prev]);
      return saved;
    } catch {
      toast.error('Failed to save insight');
      return null;
    }
  }, []);

  const removeInsight = useCallback(async (id: number): Promise<void> => {
    try {
      await deleteInsight(id);
      setInsights((prev) => prev.filter((i) => i.id !== id));
    } catch {
      toast.error('Failed to delete insight');
    }
  }, []);

  return { insights, isLoading, error, addInsight, removeInsight, refetch };
}